// app/signup/page.tsx
'use client'

import { FormEvent, Suspense, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SignInButton } from '@/components/auth/SignInButton'
import { ReferralBanner } from '@/components/referral/ReferralBanner'
import { storeRefCode } from '@/lib/referral/ref-code'

// The signup endpoint is leak-safe: it always returns 200 OK whether
// the email is new, already-unverified, or already-verified. So the
// success message is generic ("check your email") and we don't try
// to differentiate "you already have an account" — that would
// reintroduce the enumeration leak we just closed.

// `?callbackUrl` (read inside <SignupContent/>) is honored by the
// Google OAuth path (passed into SignInButton). It can't be honored
// directly by the credentials-signup path because we don't sign the
// user in until they verify their email — but /signin already
// honors callbackUrl, so the post-verify sign-in step takes them
// to the right place. See followup note inline.

export default function SignupPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-dvh grid place-items-center text-muted-foreground">Loading…</div>
      }
    >
      <SignupContent />
    </Suspense>
  )
}

function SignupContent() {
  // Honor ?callbackUrl=. Falls back to /dashboard so the page works
  // exactly as before when no param is supplied.
  const searchParams = useSearchParams()
  const callbackUrl = searchParams.get('callbackUrl') ?? '/dashboard'
  const ref = searchParams.get('ref') ?? undefined

  // (a) Persist the referral code so it survives navigation and the
  // Google OAuth round-trip. See src/lib/ref-code.ts for the dual
  // sessionStorage + cookie strategy (REF-8 / #515).
  useEffect(() => {
    if (!ref) return
    storeRefCode(ref)
  }, [ref])

  // Non-undefined form of `ref` for components that need a string prop
  // (e.g. <ReferralBanner code={refCode} />). Derived from `ref` so the
  // storage + display always agree.
  const refCode = ref ?? ''

  // Neutral age screen (#817): a birth-year dropdown. The server gates
  // under-13 — we never create an account for them.
  const currentYear = new Date().getFullYear()
  const birthYears = Array.from({ length: 100 }, (_, i) => currentYear - i)

  const [pending, setPending] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  // Submitted email is held for the success confirmation — shown in the
  // "we sent a link to X" line and pre-filled into the resend form.
  const [submittedEmail, setSubmittedEmail] = useState('')

  // On success we swap the whole form out for a focused "check your email"
  // confirmation (so the user isn't left staring at the fields they just
  // filled, wondering if anything happened). Move focus to it for SR users.
  const confirmRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (submitted) confirmRef.current?.focus()
  }, [submitted])

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setPending(true)
    setMsg(null)

    // Use form.elements.namedItem (the standards path also used by
    // signin-content.tsx) instead of the browser-only `form.email`
    // convenience accessor — the latter is unreliable in jsdom, which
    // made the email-signup submit path effectively untested before.
    const form = e.currentTarget
    const nameVal = (form.elements.namedItem('name') as HTMLInputElement | null)?.value ?? ''
    const emailVal = (form.elements.namedItem('email') as HTMLInputElement | null)?.value ?? ''
    const passwordVal =
      (form.elements.namedItem('password') as HTMLInputElement | null)?.value ?? ''
    // Forward the referral code so /api/auth/signup attributes the
    // referral at user-create time. REF-8 (#515) stores the code in a
    // cookie + sessionStorage on /signup mount, and the API route reads
    // the cookie as a fallback — but we forward `ref` in the body too as
    // belt-and-suspenders for (a) clients that strip the cookie between
    // mount and submit, and (b) explicitness in the request payload
    // (cookie-only attribution is harder to grep for and debug).
    const birthYearVal =
      (form.elements.namedItem('birthYear') as HTMLSelectElement | null)?.value ?? ''
    const body = {
      name: nameVal,
      email: emailVal,
      password: passwordVal,
      birthYear: birthYearVal,
      ref,
      callbackUrl,
    }

    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      if (res.ok) {
        setSubmitted(true)
        setSubmittedEmail(body.email)
        // The confirmation view itself says "check your email", so clear msg —
        // it's reused only for resend results + errors from here on.
        setMsg(null)
      } else {
        // 4xx: validation failure (e.g. password too short). Show the
        // server's generic message — it never differentiates by email.
        const data = await res.json().catch(() => ({}))
        setMsg(
          typeof data?.error === 'string' ? data.error : 'Could not sign up. Please try again.',
        )
      }
    } catch {
      setMsg('Network error. Please try again.')
    } finally {
      setPending(false)
    }
  }

  async function onResend(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = e.currentTarget
    const email = (form.elements.namedItem('email') as HTMLInputElement | null)?.value ?? ''
    try {
      const res = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, callbackUrl }),
      })
      const data = await res.json().catch(() => ({}))
      // The endpoint returns the SAME message for all internal cases
      // (deliberately leak-safe). We just surface it.
      setMsg(
        typeof data?.message === 'string'
          ? data.message
          : "If we have an account for that email, we've sent a verification link.",
      )
    } catch {
      setMsg('Network error. Please try again.')
    }
  }

  return (
    <main className="min-h-dvh bg-background text-foreground">
      <div className="mx-auto max-w-md px-4 py-12">
        <ReferralBanner code={refCode} />
        <Card className="border-border/70 bg-card/60">
          <CardHeader>
            <CardTitle className="text-foreground">
              {submitted ? 'Check your email' : 'Create your free account'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {submitted ? (
              <div ref={confirmRef} tabIndex={-1} role="status" className="space-y-4 outline-none">
                <div className="flex flex-col items-center text-center">
                  <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand/15 text-brand-ink">
                    <Mail className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <p className="text-sm text-foreground">We sent a verification link to</p>
                  {/* Email on its own line in a chip so a long address can't wrap
                      mid-word inside the sentence. */}
                  <p className="mt-1.5">
                    <span className="inline-block max-w-full break-all rounded-md bg-muted px-2.5 py-1 text-sm font-medium text-foreground">
                      {submittedEmail}
                    </span>
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Click the link in that email to activate your account and start studying.
                  </p>
                </div>

                <div className="rounded-md border border-border/60 bg-card/40 p-3">
                  <p className="text-sm font-medium text-foreground">Didn&apos;t get the email?</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Check your spam folder, or request a new verification link below.
                  </p>
                  <form onSubmit={onResend} className="mt-3 flex gap-2">
                    <input
                      type="email"
                      name="email"
                      aria-label="Email address"
                      defaultValue={submittedEmail}
                      required
                      className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    />
                    <Button
                      type="submit"
                      variant="outline"
                      className="border-brand/40 text-brand-ink hover:bg-brand/10 dark:text-brand-ink"
                    >
                      Resend
                    </Button>
                  </form>
                  {msg && (
                    <p className="mt-2 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                      {msg}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitted(false)
                      setMsg(null)
                    }}
                    className="underline decoration-slate-500 hover:text-foreground"
                  >
                    Use a different email
                  </button>
                  <Link
                    href={`/signin?callbackUrl=${encodeURIComponent(callbackUrl)}`}
                    className="underline decoration-slate-500 hover:text-foreground"
                  >
                    Sign in
                  </Link>
                </div>
              </div>
            ) : (
              <>
                {/* Google OAuth. Uses the shared SignInButton component so
                /signup and /signin look identical (brand gradient, same
                width, same label) and route through NextAuth's signIn()
                SDK call rather than a hardcoded redirect URL.
                Previously this was an inline `<Button bg-white>` which
                rendered as a borderless white-on-white smear on the
                light-mode card. */}
                {/* (b) Pass ref so SignInButton stashes it in a cookie immediately
                before the Google OAuth redirect, guaranteeing survival
                even if the useEffect above fires after the click. */}
                <SignInButton callbackUrl={callbackUrl} referralCode={ref} />

                <div className="relative text-center text-muted-foreground">
                  <span className="px-2 bg-card/60">or</span>
                  <div className="absolute left-0 right-0 top-1/2 -z-10 h-px bg-slate-700" />
                </div>

                {/* Email + Password */}
                <form className="space-y-3" onSubmit={onSubmit}>
                  <div>
                    <label htmlFor="signup-name" className="block text-sm text-muted-foreground">
                      Name
                    </label>
                    <input
                      id="signup-name"
                      name="name"
                      className="mt-1 w-full rounded-md border border-border bg-card/40 px-3 py-2 text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    />
                  </div>
                  <div>
                    <label htmlFor="signup-email" className="block text-sm text-muted-foreground">
                      Email
                    </label>
                    <input
                      id="signup-email"
                      type="email"
                      name="email"
                      required
                      className="mt-1 w-full rounded-md border border-border bg-card/40 px-3 py-2 text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="signup-password"
                      className="block text-sm text-muted-foreground"
                    >
                      Password
                    </label>
                    <input
                      id="signup-password"
                      type="password"
                      name="password"
                      required
                      minLength={8}
                      className="mt-1 w-full rounded-md border border-border bg-card/40 px-3 py-2 text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="signup-birth-year"
                      className="block text-sm text-muted-foreground"
                    >
                      Birth year
                    </label>
                    <select
                      id="signup-birth-year"
                      name="birthYear"
                      required
                      defaultValue=""
                      className="mt-1 w-full rounded-md border border-border bg-card/40 px-3 py-2 text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <option value="" disabled>
                        Select year
                      </option>
                      {birthYears.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-muted-foreground">
                      You must be at least 13 years old to create an account.
                    </p>
                  </div>

                  <Button disabled={pending} className="w-full bg-brand text-on-brand">
                    {pending ? 'Creating...' : 'Create account'}
                  </Button>

                  <p className="text-xs text-muted-foreground">
                    By creating an account, you agree to our{' '}
                    <Link className="underline decoration-slate-500" href="/terms">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link className="underline decoration-slate-500" href="/privacy">
                      Privacy Policy
                    </Link>
                    .
                  </p>

                  {msg && (
                    <p
                      role={submitted ? 'status' : 'alert'}
                      aria-live={submitted ? 'polite' : 'assertive'}
                      className={
                        submitted
                          ? 'text-sm font-medium text-emerald-600 dark:text-emerald-400'
                          : 'text-sm font-medium text-red-600 dark:text-red-400'
                      }
                    >
                      {msg}
                    </p>
                  )}

                  <p className="text-xs text-muted-foreground">
                    Already have an account?{' '}
                    <Link
                      className="underline decoration-slate-500"
                      // Preserve callbackUrl across the signup ↔ signin
                      // toggle so a user who clicked "Create free
                      // account" but actually wanted to sign in ends
                      // up at the same destination.
                      href={`/signin?callbackUrl=${encodeURIComponent(callbackUrl)}`}
                    >
                      Sign in
                    </Link>
                  </p>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  )
}
